import { useState, useEffect } from "react";
import { Check, Edit3, ChevronRight } from "lucide-react";
import { PageHeader } from "../../components/shared/PageHeader";
import { Card } from "../../components/shared/Card";
import { Btn } from "../../components/shared/Btn";
import { useAppData } from "../../lib/AppDataContext";
import { performanceTargetsApi } from "../../lib/performanceTargetsApi";
import { MonthPicker } from "../../components/shared/MonthPicker";
import { YearPicker } from "../../components/shared/YearPicker";
import { monthLabelToYM, ymToMonthLabel } from "../../lib/bookingsApi";
import { reachedVisit } from "../../lib/attendance";
import { useAuth } from "../../lib/AuthContext";

/* Helper: lead.assignedTo can be a single string ("s1") or array.
   Returns whether the given id is one of the assignees. */
function isAssignedTo(lead, id) {
  if (!lead?.assignedTo) return false;
  if (Array.isArray(lead.assignedTo)) return lead.assignedTo.includes(id);
  return lead.assignedTo === id;
}

/* Stage counting for the Visit / Token / Booking targets.
 *
 * The three behave differently, by design:
 *
 *   VISIT   persists once earned. Reaching visited, token OR booked counts
 *           it for good -- moving the lead back to new, contacted,
 *           follow-up, hold or lead-lost does not take it away.
 *
 *   TOKEN   counts while the lead is CURRENTLY at token or booked AND its
 *           history shows it actually passed through token. A lead taken
 *           straight to booked never had a token, so it does not count one;
 *           moved back from booked to token, it does.
 *
 *   BOOKED  counts only while the lead is CURRENTLY booked.
 *
 * Token and booked therefore fall away when a lead moves backward, while
 * visit stays. Previously everReached() made all three permanent, so a
 * count could only ever rise.
 *
 * hold and lead_lost are treated like new/contacted -- below token, so
 * neither counts. Membership sets rather than index arithmetic on STATUSES:
 * hold and lead_lost sit AFTER booked in that array, so "index >= booked"
 * would wrongly count a lost lead as booked.
 *
 * statusHistory comes from the leads list as [{ toStatus, createdAt }]
 * ascending, capped at 50 rows (leads.service.ts:106). A row is written on
 * create and on every status change by any role -- admin, super admin or
 * the assigned employee all take the same update() path -- so the
 * "did it pass through" test is reliable. */
const AT_OR_BEYOND_TOKEN = new Set(["token", "booked"]);
const REACHED_VISIT = new Set(["visited", "token", "booked"]);

/* Visits are EVENTS, so this returns a count rather than a boolean: one
   client can visit several projects, and a lead that returns to visited
   after being lost has visited twice.
 *
 * A token or booking implies a visit happened even when nobody recorded
 * the visited step -- but only ONE per unvisited run. Ashok's real path
 *   contacted > booked > token > booked > lead_lost > new > visited > new > visited
 * is 3 visits: one implied by that first booked/token/booked progression,
 * then the two genuine visits. Not 5.
 *
 * `pending` tracks whether the current run has already been credited. It
 * resets when the lead drops back below visited, so a later unvisited climb
 * to token counts again. */
/* One walk over a lead's history, tallying the three metrics for ONE month.
 *
 * The whole history is walked so the running state -- has this climb already
 * been credited a visit, was the lead in token/booked -- is correct at the
 * month boundary. Only transitions DATED in `month` ("YYYY-MM") are tallied.
 * A lead that reached token in August and moves to booked in September must
 * not add an implied visit in September: the run was credited in August.
 *
 * The three behave differently, as specified:
 *
 *   VISIT   an EVENT. +1 per `visited`, +1 per climb to token/booked that
 *           was never credited a visit. Never subtracts -- a site visit
 *           happened, and losing the lead afterwards does not un-visit it.
 *           One client can visit several projects, so a lead can count more
 *           than once.
 *
 *   TOKEN   a STATE. +1 entering token, -1 leaving token/booked. So a lead
 *           tokened and then lost in the same month nets 0.
 *
 *   BOOKED  a STATE. +1 entering booked, -1 leaving booked.
 *
 * Token and booked are floored at 0: a lead booked in August and reversed in
 * September would otherwise give September -1. August keeps its 1 -- the
 * target was met at the time, and past months never change retroactively.
 *
 * createdAt arrives as an ISO string (leadsApi passes statusHistory through
 * untouched), so slice(0,7) is the month key. */
function tallyForMonth(lead, month) {
  const hist = lead?.statusHistory;

  // No history at all -- the rows were cleared, or the lead predates status
  // tracking. Fall back to its current status, but attribute it to the month
  // the lead was CREATED in. Without that date check the fallback counted
  // toward every month the picker could show, past and future alike, so a
  // lead sitting at visited added 1 to March, September and everything
  // between.
  //
  // leadsApi runs createdAt through parseDate, so it arrives as a Date, not
  // the ISO string the history rows carry.
  if (!Array.isArray(hist) || hist.length === 0) {
    const created = lead?.createdAt;
    const createdMonth = created instanceof Date
      ? created.toISOString().slice(0, 7)
      : String(created || "").slice(0, 7);
    if (createdMonth !== month) return { visit: 0, token: 0, booked: 0 };
    const st = lead?.status;
    return {
      visit: REACHED_VISIT.has(st) ? 1 : 0,
      token: AT_OR_BEYOND_TOKEN.has(st) ? 1 : 0,
      booked: st === "booked" ? 1 : 0,
    };
  }

  let visit = 0, token = 0, booked = 0;
  let credited = false;   // current climb already counted a visit
  let inToken = false;    // lead is at token or booked
  let inBooked = false;

  for (const h of hist) {
    const st = h.toStatus || h.status;
    const inMonth = String(h.createdAt || "").slice(0, 7) === month;

    if (st === "visited") {
      if (inMonth) visit += 1;
      credited = true;
      if (inToken && inMonth) token -= 1;
      if (inBooked && inMonth) booked -= 1;
      inToken = false;
      inBooked = false;
    } else if (st === "token") {
      if (!credited && inMonth) visit += 1;
      credited = true;
      if (!inToken && inMonth) token += 1;
      if (inBooked && inMonth) booked -= 1;
      inToken = true;
      inBooked = false;
    } else if (st === "booked") {
      if (!credited && inMonth) visit += 1;
      credited = true;
      if (!inToken && inMonth) token += 1;
      if (!inBooked && inMonth) booked += 1;
      inToken = true;
      inBooked = true;
    } else {
      // new, contacted, follow_up, re_visit, weekend, hold, lead_lost
      if (inToken && inMonth) token -= 1;
      if (inBooked && inMonth) booked -= 1;
      credited = false;
      inToken = false;
      inBooked = false;
    }
  }

  return {
    visit,
    token: Math.max(0, token),
    booked: Math.max(0, booked),
  };
}

/* Performance tracker — Visit / Token / Booking targets per salesperson.
   Targets are set by admin; Visited/Tokens/Booked counts are derived
   from the live lead pipeline. Pure SVG charts — no chart library. */
export function SalesTeamPerformance({ role = "super_admin" }) {
  const { allSalesPeople, leadsData } = useAppData();

  /* Targets live in performance_targets -- one row per employee per month --
     and are fetched here rather than through AppDataContext, which has no
     month dimension for them. Adding a second month concept beside
     billingMonth would put more shared state in a file several screens
     depend on. The month that scopes the counts scopes the targets too.

     A month with no row means NO TARGET SET; it renders as 0 until an admin
     enters one. Progress is unaffected -- it derives from lead transitions
     dated in the month and is already live. */
  const [targetsByEmp, setTargetsByEmp] = useState({});
  const [targetOverlay, setTargetOverlay] = useState({});

  // For sales role, hard-coded "me" = s1 (Sarika). Once auth ships, this
  // becomes req.user.id.
  const { user } = useAuth();
  // Logged-in user id (matches lead.assignedTo which stores user ids).
  const SALES_ME_ID = user?.id || null;
  const isSalesView = role === "employee";
  const [editing, setEditing] = useState(false);
  const isEditing = !isSalesView && editing;
  const [salesScope, setSalesScope] = useState("mine"); // "mine" | "all"

  // Scopes both the counts and the targets. MonthPicker speaks
  // "September 2026"; the API keys on "2026-09", so the two converters from
  // bookingsApi bridge them rather than a second pair being written here.
  const [perfMonth, setPerfMonth] = useState(() => new Date().toISOString().slice(0, 7));

  /* Yearly view sums the twelve months of the Indian financial year,
     1 April to 31 March -- the same boundary the Billing sections use.
     Targets and counts both sum: a year's target is whatever the monthly
     targets add up to, not a separate figure, so there is nothing to edit
     in this view and the Edit button hides. */
  const [perfView, setPerfView] = useState("monthly"); // "monthly" | "yearly"
  const isYearlyPerf = perfView === "yearly";

  const currentFyStart = (() => {
    const now = new Date();
    return now.getMonth() < 3 ? now.getFullYear() - 1 : now.getFullYear();
  })();
  const [perfFyStart, setPerfFyStart] = useState(currentFyStart);

  const fyMonthKeys = Array.from({ length: 12 }, (_, i) => {
    const m = (3 + i) % 12;                     // 3 = April
    const y = i < 9 ? perfFyStart : perfFyStart + 1;
    return `${y}-${String(m + 1).padStart(2, "0")}`;
  });

  const fyYearOptions = [];
  for (let y = currentFyStart; y >= 2020; y--) fyYearOptions.push(y);

  useEffect(() => {
    let alive = true;
    if (!isYearlyPerf) {
      performanceTargetsApi.listByMonth(perfMonth)
        .then((m) => { if (alive) { setTargetsByEmp(m); setTargetOverlay({}); } })
        .catch(() => { if (alive) setTargetsByEmp({}); });
      return () => { alive = false; };
    }
    // Yearly: twelve fetches, summed per employee. A month with no row
    // contributes nothing rather than a zero, so an unset month never drags
    // the yearly figure down.
    Promise.all(fyMonthKeys.map((m) => performanceTargetsApi.listByMonth(m).catch(() => ({}))))
      .then((maps) => {
        if (!alive) return;
        const summed = {};
        for (const map of maps) {
          for (const [id, t] of Object.entries(map)) {
            const acc = summed[id] || { visitTarget: 0, tokenTarget: 0, bookingTarget: 0 };
            summed[id] = {
              visitTarget: acc.visitTarget + (t.visitTarget || 0),
              tokenTarget: acc.tokenTarget + (t.tokenTarget || 0),
              bookingTarget: acc.bookingTarget + (t.bookingTarget || 0),
            };
          }
        }
        setTargetsByEmp(summed);
        setTargetOverlay({});
      });
    return () => { alive = false; };
    // fyMonthKeys is derived from perfFyStart; listing it would refire every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [perfMonth, isYearlyPerf, perfFyStart]);

  // Stored targets with any in-flight edit layered on top.
  const perfTargets = (() => {
    const base = {};
    for (const p of (allSalesPeople || [])) {
      base[p.id] = targetsByEmp[p.id] || { visitTarget: 0, tokenTarget: 0, bookingTarget: 0 };
    }
    for (const [id, patch] of Object.entries(targetOverlay)) {
      base[id] = { ...(base[id] || { visitTarget: 0, tokenTarget: 0, bookingTarget: 0 }), ...patch };
    }
    return base;
  })();

  const setPerfTarget = (id, key, value) => {
    const v = Math.max(0, parseInt(value) || 0);
    setTargetOverlay((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), [key]: v } }));
  };

  /* Persist every edited row, then refetch so the screen shows what the
     server actually stored. The API merges, so sending all three is safe. */
  const commitAllPerfTargets = async () => {
    const ids = Object.keys(targetOverlay);
    for (const id of ids) {
      const t = perfTargets[id];
      try {
        await performanceTargetsApi.upsert(id, perfMonth, {
          visitTarget: t.visitTarget,
          tokenTarget: t.tokenTarget,
          bookingTarget: t.bookingTarget,
        });
      } catch (e) {
        console.error("[perf] target save failed for", id, e);
      }
    }
    if (ids.length) {
      const fresh = await performanceTargetsApi.listByMonth(perfMonth).catch(() => ({}));
      setTargetsByEmp(fresh);
    }
    setTargetOverlay({});
  };

  // Build per-person rows.
  //
  // Filter FIRST, on both flags. allSalesPeople comes from
  // refreshEmployees({ active: "all" }) and deliberately includes archived
  // staff so payroll and attendance keep their history -- but a performance
  // table should only show people currently working. employees.active is set
  // by the Employees archive action, users.active by Settings > Users, and
  // the two drift (see employeesApi.js), so both are checked.
  //
  // rows is the single source for the table, teamTotals (line ~76) and all
  // three charts, so filtering here clears every one of them consistently.
  const rows = allSalesPeople
    .filter((s) => s.active !== false && s.userActive !== false)
    .map((person) => {
    const target = perfTargets[person.id] || { visitTarget: 0, tokenTarget: 0, bookingTarget: 0 };
    // Leads store USER ids in assignedTo; match person.userId (fallback id).
    const linkId = person.userId || person.id;
    const myLeads = leadsData.filter((l) => isAssignedTo(l, linkId));
    // Monthly: one tally. Yearly: the same tally over each of the FY's
    // twelve months, summed -- so a token opened in May and reversed in
    // June nets to zero across the year, exactly as it does within a month.
    const monthsToCount = isYearlyPerf ? fyMonthKeys : [perfMonth];
    const tally = myLeads.reduce((acc, l) => {
      for (const m of monthsToCount) {
        const t = tallyForMonth(l, m);
        acc.visit += t.visit; acc.token += t.token; acc.booked += t.booked;
      }
      return acc;
    }, { visit: 0, token: 0, booked: 0 });
    const visited = tally.visit;
    const tokens = tally.token;
    const booked = tally.booked;
    return {
      ...person,
      visitTarget: target.visitTarget,
      tokenTarget: target.tokenTarget || 0,
      bookingTarget: target.bookingTarget,
      visited, tokens, booked,
      visitPct: target.visitTarget > 0 ? Math.min(100, Math.round((visited / target.visitTarget) * 100)) : 0,
      tokenPct: (target.tokenTarget || 0) > 0 ? Math.min(100, Math.round((tokens / target.tokenTarget) * 100)) : 0,
      bookingPct: target.bookingTarget > 0 ? Math.min(100, Math.round((booked / target.bookingTarget) * 100)) : 0,
      visitOverTarget: target.visitTarget > 0 && visited > target.visitTarget,
      tokenOverTarget: (target.tokenTarget || 0) > 0 && tokens > (target.tokenTarget || 0),
      bookingOverTarget: target.bookingTarget > 0 && booked > target.bookingTarget,
    };
  });

  // Stable order so the list doesn't jump after a target save/refresh.
  rows.sort((a, b) => (a.name || "").localeCompare(b.name || ""));

  const visibleRows = isSalesView
    ? (salesScope === "mine" ? rows.filter((r) => (r.userId || r.id) === SALES_ME_ID) : rows)
    : rows;

  const teamTotals = !isSalesView ? rows.reduce((acc, r) => ({
    visitTarget: acc.visitTarget + r.visitTarget,
    tokenTarget: acc.tokenTarget + r.tokenTarget,
    bookingTarget: acc.bookingTarget + r.bookingTarget,
    visited: acc.visited + r.visited,
    tokens: acc.tokens + r.tokens,
    booked: acc.booked + r.booked,
  }), { visitTarget: 0, tokenTarget: 0, bookingTarget: 0, visited: 0, tokens: 0, booked: 0 }) : null;

  // SVG chart sizing.
  const chartHeight = 220;
  const chartBarMaxHeight = 160;
  const maxVisit = Math.max(1, ...visibleRows.map((r) => Math.max(r.visitTarget, r.visited)));
  const maxToken = Math.max(1, ...visibleRows.map((r) => Math.max(r.tokenTarget, r.tokens)));
  const maxBooking = Math.max(1, ...visibleRows.map((r) => Math.max(r.bookingTarget, r.booked)));

  /* SVG chart renderer — paired bars per salesperson. */
  const renderChart = (title, subtitle, max, getTarget, getActual, getPct, getOver, actualClass, overClass) => (
    <Card className="p-5">
      <h4 className="text-sm font-semibold text-slate-900 mb-1">{title}</h4>
      <p className="text-xs text-slate-500 mb-4">{subtitle}</p>
      {visibleRows.length === 0 ? (
        <div className="py-8 text-center text-xs text-slate-400">No data</div>
      ) : (
        <div className="overflow-x-auto">
          <svg width={Math.max(280, visibleRows.length * 90)} height={chartHeight} className="block">
            {visibleRows.map((r, i) => {
              const groupX = 30 + i * 90;
              const targetH = max > 0 ? (getTarget(r) / max) * chartBarMaxHeight : 0;
              const actualH = max > 0 ? (getActual(r) / max) * chartBarMaxHeight : 0;
              const baseY = chartHeight - 40;
              const over = getOver(r);
              return (
                <g key={r.id}>
                  <rect x={groupX} y={baseY - targetH} width={28} height={targetH} className="fill-slate-300" rx={3} />
                  <text x={groupX + 14} y={baseY - targetH - 4} textAnchor="middle"
                    className="fill-slate-500 text-[10px] font-semibold tabular-nums">
                    {getTarget(r)}
                  </text>
                  <rect x={groupX + 32} y={baseY - actualH} width={28} height={actualH}
                    className={over ? overClass : actualClass} rx={3} />
                  <text x={groupX + 46} y={baseY - actualH - 4} textAnchor="middle"
                    className={`text-[10px] font-bold tabular-nums ${over ? "fill-emerald-700" : "fill-slate-700"}`}>
                    {getActual(r)}
                  </text>
                  <text x={groupX + 30} y={chartHeight - 22} textAnchor="middle"
                    className="fill-slate-700 text-[11px] font-medium">
                    {r.name.split(" ")[0]}
                  </text>
                  <text x={groupX + 30} y={chartHeight - 8} textAnchor="middle"
                    className="fill-slate-400 text-[10px]">
                    {getPct(r)}%
                  </text>
                </g>
              );
            })}
            <line x1={20} y1={chartHeight - 40}
              x2={Math.max(280, visibleRows.length * 90) - 10} y2={chartHeight - 40}
              className="stroke-slate-300" strokeWidth="1" />
          </svg>
        </div>
      )}
    </Card>
  );

  return (
    <div>
      <PageHeader accent="sales"
        title="Sales Team · Performance"
        subtitle={isSalesView
          ? (salesScope === "mine"
              ? "Your monthly targets and live progress against them"
              : "Team-wide performance · everyone's progress against their targets")
          : "Set per-rep targets · live progress reflects lead pipeline movement"}
        actions={isSalesView ? (
          <div className="inline-flex rounded-lg ring-1 ring-inset ring-slate-300 bg-white p-0.5">
            <button onClick={() => setSalesScope("mine")}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                salesScope === "mine" ? "bg-[#162a52] text-white" : "text-slate-600 hover:bg-slate-100"
              }`}>
              Mine only
            </button>
            <button onClick={() => setSalesScope("all")}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                salesScope === "all" ? "bg-[#162a52] text-white" : "text-slate-600 hover:bg-slate-100"
              }`}>
              Everyone
            </button>
          </div>
        ) : null}
      />

      {/* Team aggregates strip — admin only. Six cards. */}
      {!isSalesView && teamTotals && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-5">
          <Card className="p-4">
            <div className="text-xs font-medium text-slate-500">Team Visit Target</div>
            <div className="text-2xl font-semibold text-slate-900 tabular-nums mt-1">{teamTotals.visitTarget}</div>
            <div className="text-[10px] text-slate-400 mt-0.5">across {allSalesPeople.filter((s) => s.active !== false && s.userActive !== false).length} reps</div>
          </Card>
          <Card className="p-4">
            <div className="text-xs font-medium text-slate-500">Team Visited</div>
            <div className="text-2xl font-semibold text-blue-700 tabular-nums mt-1">{teamTotals.visited}</div>
            <div className="text-[10px] text-slate-400 mt-0.5">
              {teamTotals.visitTarget > 0 ? `${Math.round((teamTotals.visited / teamTotals.visitTarget) * 100)}% of target` : "no target set"}
            </div>
          </Card>
          <Card className="p-4">
            <div className="text-xs font-medium text-slate-500">Team Token Target</div>
            <div className="text-2xl font-semibold text-slate-900 tabular-nums mt-1">{teamTotals.tokenTarget}</div>
            <div className="text-[10px] text-slate-400 mt-0.5">across {allSalesPeople.filter((s) => s.active !== false && s.userActive !== false).length} reps</div>
          </Card>
          <Card className="p-4">
            <div className="text-xs font-medium text-slate-500">Team Tokens</div>
            <div className="text-2xl font-semibold text-amber-700 tabular-nums mt-1">{teamTotals.tokens}</div>
            <div className="text-[10px] text-slate-400 mt-0.5">
              {teamTotals.tokenTarget > 0 ? `${Math.round((teamTotals.tokens / teamTotals.tokenTarget) * 100)}% of target` : "no target set"}
            </div>
          </Card>
          <Card className="p-4">
            <div className="text-xs font-medium text-slate-500">Team Booking Target</div>
            <div className="text-2xl font-semibold text-slate-900 tabular-nums mt-1">{teamTotals.bookingTarget}</div>
            <div className="text-[10px] text-slate-400 mt-0.5">across {allSalesPeople.filter((s) => s.active !== false && s.userActive !== false).length} reps</div>
          </Card>
          <Card className="p-4">
            <div className="text-xs font-medium text-slate-500">Team Booked</div>
            <div className="text-2xl font-semibold text-emerald-700 tabular-nums mt-1">{teamTotals.booked}</div>
            <div className="text-[10px] text-slate-400 mt-0.5">
              {teamTotals.bookingTarget > 0 ? `${Math.round((teamTotals.booked / teamTotals.bookingTarget) * 100)}% of target` : "no target set"}
            </div>
          </Card>
        </div>
      )}

      {/* Three SVG bar charts side-by-side */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        {renderChart(
          "Visit progress", "Slate = target · Blue = visited",
          maxVisit,
          (r) => r.visitTarget, (r) => r.visited, (r) => r.visitPct, (r) => r.visitOverTarget,
          "fill-blue-500", "fill-emerald-500"
        )}
        {renderChart(
          "Token progress", "Slate = target · Amber = tokens taken",
          maxToken,
          (r) => r.tokenTarget, (r) => r.tokens, (r) => r.tokenPct, (r) => r.tokenOverTarget,
          "fill-amber-600", "fill-emerald-500"
        )}
        {renderChart(
          "Booking progress", "Slate = target · Green = booked",
          maxBooking,
          (r) => r.bookingTarget, (r) => r.booked, (r) => r.bookingPct, (r) => r.bookingOverTarget,
          "fill-emerald-600", "fill-emerald-500"
        )}
      </div>

      {/* Per-rep performance table */}
      <Card className="mb-6">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between flex-wrap gap-2">
          <div>
            <h3 className="text-sm font-semibold text-slate-900">
              {isSalesView ? (salesScope === "mine" ? "My performance" : "Team performance") : "Per-rep performance"}
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              {isSalesView
                ? "Targets are set by your admin. Visited and Booked counts update as you progress leads."
                : isEditing
                  ? "Targets are now editable inline. Click Done to finish."
                  : "Click Edit to update targets. Visited and Booked are read from the live lead pipeline."}
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <div className="inline-flex rounded-lg ring-1 ring-inset ring-slate-300 overflow-hidden">
                <button type="button" onClick={() => { setPerfView("monthly"); setEditing(false); }}
                  className={`text-xs font-medium px-3 py-1.5 transition-colors ${!isYearlyPerf ? "bg-[#162a52] text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}>
                  Monthly
                </button>
                <button type="button" onClick={() => { setPerfView("yearly"); setEditing(false); }}
                  className={`text-xs font-medium px-3 py-1.5 transition-colors ${isYearlyPerf ? "bg-[#162a52] text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}>
                  Yearly
                </button>
              </div>
              {isYearlyPerf ? (
                <YearPicker value={perfFyStart} onChange={setPerfFyStart} options={fyYearOptions} align="left" />
              ) : (
                <MonthPicker
                  value={ymToMonthLabel(perfMonth)}
                  onChange={(label) => setPerfMonth(monthLabelToYM(label) || perfMonth)}
                  align="left"
                />
              )}
            </div>
          </div>
          {!isSalesView && !isYearlyPerf && (
            isEditing ? (
              <Btn variant="primary" size="sm" onClick={async () => { try { await commitAllPerfTargets(); } catch { /* toast handled in ctx */ } setEditing(false); }}>
                <Check className="w-3.5 h-3.5" />Done
              </Btn>
            ) : (
              <Btn variant="secondary" size="sm" onClick={() => setEditing(true)}>
                <Edit3 className="w-3.5 h-3.5" />Edit
              </Btn>
            )
          )}
        </div>
        <div className="overflow-auto max-h-[70vh]">
          <table className="w-full text-sm">
            <thead>
              <tr className="sticky top-0 z-20 text-left text-xs font-medium text-slate-500 bg-slate-50 border-b border-slate-200">
                <th className="px-3 py-2.5">Staff</th>
                <th className="px-3 py-2.5 text-right">Visit Target</th>
                <th className="px-3 py-2.5 text-right">Visited</th>
                <th className="px-3 py-2.5">Visit Progress</th>
                <th className="px-3 py-2.5 text-right">Token Target</th>
                <th className="px-3 py-2.5 text-right">Tokens</th>
                <th className="px-3 py-2.5">Token Progress</th>
                <th className="px-3 py-2.5 text-right">Booking Target</th>
                <th className="px-3 py-2.5 text-right">Booked</th>
                <th className="px-3 py-2.5">Booking Progress</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {visibleRows.map((r) => (
                <tr key={r.id} className="hover:bg-slate-50">
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-slate-100 grid place-items-center text-xs font-semibold text-slate-600">
                        {r.name.split(" ").map((p) => p[0]).join("").slice(0, 2)}
                      </div>
                      <div>
                        <div className="font-medium text-slate-900">{r.name}</div>
                        <div className="text-xs text-slate-500">{r.email}</div>
                      </div>
                    </div>
                  </td>
                  {/* Visit */}
                  <td className="px-3 py-2.5 text-right">
                    {isEditing ? (
                      <input type="number" min="0" value={r.visitTarget === 0 ? "" : r.visitTarget} placeholder="0"
                        onChange={(e) => setPerfTarget(r.id, "visitTarget", e.target.value)}
                        className="w-20 text-right text-sm tabular-nums rounded ring-1 ring-inset ring-slate-300 hover:ring-slate-400 focus:ring-blue-500 px-2 py-1 bg-white focus:outline-none" />
                    ) : (
                      <span className="font-semibold tabular-nums text-slate-900">{r.visitTarget}</span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums font-semibold text-blue-700">
                    {r.visited}
                    {r.visitOverTarget && <span className="ml-1 text-[10px] font-bold text-emerald-700 bg-emerald-50 ring-1 ring-inset ring-emerald-600/20 rounded px-1 py-0.5">+over</span>}
                  </td>
                  <td className="px-3 py-2.5 min-w-[140px]">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full ${
                          r.visitOverTarget ? "bg-emerald-500"
                          : r.visitPct >= 75 ? "bg-blue-500"
                          : r.visitPct >= 40 ? "bg-amber-500" : "bg-red-400"
                        }`} style={{ width: `${r.visitPct}%` }} />
                      </div>
                      <span className="text-[11px] text-slate-500 tabular-nums w-9 text-right">{r.visitPct}%</span>
                    </div>
                  </td>
                  {/* Token */}
                  <td className="px-3 py-2.5 text-right">
                    {isEditing ? (
                      <input type="number" min="0" value={r.tokenTarget === 0 ? "" : r.tokenTarget} placeholder="0"
                        onChange={(e) => setPerfTarget(r.id, "tokenTarget", e.target.value)}
                        className="w-20 text-right text-sm tabular-nums rounded ring-1 ring-inset ring-slate-300 hover:ring-slate-400 focus:ring-blue-500 px-2 py-1 bg-white focus:outline-none" />
                    ) : (
                      <span className="font-semibold tabular-nums text-slate-900">{r.tokenTarget}</span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums font-semibold text-amber-700">
                    {r.tokens}
                    {r.tokenOverTarget && <span className="ml-1 text-[10px] font-bold text-emerald-700 bg-emerald-50 ring-1 ring-inset ring-emerald-600/20 rounded px-1 py-0.5">+over</span>}
                  </td>
                  <td className="px-3 py-2.5 min-w-[140px]">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full ${
                          r.tokenOverTarget ? "bg-emerald-500"
                          : r.tokenPct >= 75 ? "bg-amber-500"
                          : r.tokenPct >= 40 ? "bg-amber-400" : "bg-red-400"
                        }`} style={{ width: `${r.tokenPct}%` }} />
                      </div>
                      <span className="text-[11px] text-slate-500 tabular-nums w-9 text-right">{r.tokenPct}%</span>
                    </div>
                  </td>
                  {/* Booking */}
                  <td className="px-3 py-2.5 text-right">
                    {isEditing ? (
                      <input type="number" min="0" value={r.bookingTarget === 0 ? "" : r.bookingTarget} placeholder="0"
                        onChange={(e) => setPerfTarget(r.id, "bookingTarget", e.target.value)}
                        className="w-20 text-right text-sm tabular-nums rounded ring-1 ring-inset ring-slate-300 hover:ring-slate-400 focus:ring-blue-500 px-2 py-1 bg-white focus:outline-none" />
                    ) : (
                      <span className="font-semibold tabular-nums text-slate-900">{r.bookingTarget}</span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums font-semibold text-emerald-700">
                    {r.booked}
                    {r.bookingOverTarget && <span className="ml-1 text-[10px] font-bold text-emerald-700 bg-emerald-50 ring-1 ring-inset ring-emerald-600/20 rounded px-1 py-0.5">+over</span>}
                  </td>
                  <td className="px-3 py-2.5 min-w-[140px]">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full ${
                          r.bookingOverTarget ? "bg-emerald-500"
                          : r.bookingPct >= 75 ? "bg-emerald-600"
                          : r.bookingPct >= 40 ? "bg-amber-500" : "bg-red-400"
                        }`} style={{ width: `${r.bookingPct}%` }} />
                      </div>
                      <span className="text-[11px] text-slate-500 tabular-nums w-9 text-right">{r.bookingPct}%</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
